"use client";

import {
  sportConfigForPlanner,
  type SelectPrefField,
} from "@/lib/session-planner/sport-config";
import type { SessionPreferences } from "@/lib/session-planner/types";
import { Field, Segmented } from "@/app/_components/ui/controls";
import { SportBetaBadge } from "@/app/_components/conditions/SportBetaBadge";
import {
  isSupProfileKey,
  sportPresentation,
  type SupProfileKey,
} from "@/lib/conditions/sport-profiles";

type Props = {
  prefs: SessionPreferences;
  onChange: (next: SessionPreferences) => void;
  availableSupProfiles?: readonly SupProfileKey[];
};

export function updateSessionPreference(
  prefs: SessionPreferences,
  key: string,
  value: string,
): SessionPreferences {
  return { ...prefs, values: { ...prefs.values, [key]: value } };
}

export function SessionSupProfileBeta({
  scoringKey,
  surface,
  emphasised = false,
}: {
  scoringKey: string;
  surface: "Planner" | "Finder";
  emphasised?: boolean;
}) {
  if (!isSupProfileKey(scoringKey)) return null;
  const presentation = sportPresentation(scoringKey);
  return (
    <span
      className="inline-flex items-center gap-1.5"
      aria-label={`${surface}: ${presentation.label} Beta`}
    >
      <span>{presentation.label}</span>
      <SportBetaBadge emphasised={emphasised} />
    </span>
  );
}

function SupProfileSelector({
  field,
  value,
  onChange,
}: {
  field: SelectPrefField;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {field.options.map((option) => {
        if (!isSupProfileKey(option.value)) return null;
        const presentation = sportPresentation(option.value);
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            aria-label={`${presentation.label} Beta`}
            onClick={() => onChange(option.value)}
            className={`flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border px-2.5 text-[12.5px] font-semibold transition-colors ${
              selected
                ? "border-navy bg-navy text-white"
                : "border-border-light bg-white text-text-body hover:border-gold/40"
            }`}
          >
            <SessionSupProfileBeta
              scoringKey={option.value}
              surface="Planner"
              emphasised={selected}
            />
          </button>
        );
      })}
    </div>
  );
}

export function SessionPreferencesPanel({
  prefs,
  onChange,
  availableSupProfiles = [],
}: Props) {
  const config = sportConfigForPlanner(prefs.sport, availableSupProfiles);

  const set = (key: string, value: string) =>
    onChange(updateSessionPreference(prefs, key, value));

  const visible = config.fields.filter((f) => !f.showIf || f.showIf(prefs.values));

  return (
    <div className="space-y-4">
      <h3 className="font-serif text-[17px] font-bold text-navy">Your session</h3>
      {visible.map((field) => {
        const value = prefs.values[field.key] ?? field.default;

        if (field.kind === "finite-positive-number") {
          return (
            <Field key={field.key} label={field.label}>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  name={field.key}
                  min={field.min}
                  step={field.step}
                  value={value}
                  aria-label={`${field.label} in ${field.unit}`}
                  onChange={(event) => set(field.key, event.target.value)}
                  className="min-h-[44px] w-full rounded-[10px] border border-border-light bg-white px-3.5 pr-24 text-[14px] font-medium text-text-body outline-none transition-colors focus:border-gold"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[12px] font-semibold text-text-muted">
                  {field.unit}
                </span>
              </div>
            </Field>
          );
        }

        if (
          prefs.sport === "sup" &&
          field.key === "profile" &&
          field.options.some((option) => isSupProfileKey(option.value))
        ) {
          return (
            <Field key={field.key} label={field.label}>
              <SupProfileSelector
                field={field}
                value={value}
                onChange={(next) => set(field.key, next)}
              />
            </Field>
          );
        }

        return (
          <Field key={field.key} label={field.label}>
            <Segmented
              value={value}
              options={field.options}
              onChange={(next) => set(field.key, next)}
            />
          </Field>
        );
      })}
    </div>
  );
}
